/**
 * LinkedIn connection status hooks
 *
 * useLinkedInStatus(authorId) — polls connection state
 * usePollLinkedInLogin(sessionId) — never polls (LinkedIn publishing is off)
 */

import { useState, useEffect, useCallback } from 'react';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ConnectionStatus {
  connected: boolean;
  profileUrn?: string;
  profileName?: string;
  lastValidated?: string;
  expiresAt?: string;
}

interface PollLoginResult {
  isComplete: boolean;
  credentials?: { li_at: string; jsessionid: string };
}

// ─── useLinkedInStatus ───────────────────────────────────────────────────────

export function useLinkedInStatus(authorId: string | null) {
  const [status, setStatus] = useState<ConnectionStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!authorId) return;
    // LinkedIn publishing is off on every deployment: report "not connected", make no call.
    setStatus({ connected: false });
    setLoading(false);
    setError(null);
  }, [authorId]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the compiler lint only sees this effect now that the guarded bodies are gone
    refresh();
  }, [refresh]);

  return { status, loading, error, refresh };
}

// ─── usePollLinkedInLogin ────────────────────────────────────────────────────

// The login poll never runs: LinkedIn publishing is off on every deployment.
interface PollLoginState {
  data: PollLoginResult | null;
  isPolling: boolean;
  error: string | null;
}

export function usePollLinkedInLogin(
  /* eslint-disable @typescript-eslint/no-unused-vars */
  _sessionId: string | null,
  _authorId?: string,
  _workspaceId?: string,
  /* eslint-enable @typescript-eslint/no-unused-vars */
): PollLoginState {
  const [state] = useState<PollLoginState>({ data: null, isPolling: false, error: null });
  return state;
}
